import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import {
  Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ComposedChart,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// The sleep duration + efficiency charts. The range is owned by the Sleep tab
// (SleepDataView) and passed in as `rangeDays`, so there's no selector here and
// no fixed "period average" cards — the whole view follows the one top control.
// The single-night stage breakdown appears only when the range is 1D.

// Sleep stages in a warm-to-green set (no blue/purple): deep = rich ochre,
// light = gold, REM = green, wake = grey.
const STAGE_COLORS = {
  deep: '#8A5D00',
  light: '#FFC21E',
  rem: '#10B981',
  wake: '#9ca3af',
};

const formatMinutes = (v) => {
  if (v == null || isNaN(v)) return '--';
  const hrs = Math.floor(v / 60);
  const mins = Math.round(v % 60);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

const formatHoursAxis = (v) => {
  if (v == null) return '';
  return `${(v / 60).toFixed(1)}h`;
};

const LEGEND_LABELS = {
  deep: 'Deep', light: 'Light', rem: 'REM', movingAvg: '7-day Avg',
};

const SleepChart = ({ inRange, spanDays = 30 }) => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/api/fitbit/sleep');
        if (response.status === 200) setAllData(response.data);
      } catch (error) {
        toast.error('Failed to load sleep data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    if (!allData.length) return [];
    return allData.filter(d => inRange(d.date));
  }, [allData, inRange]);

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    return {
      latest: filteredData[filteredData.length - 1],
      totalNights: filteredData.length,
    };
  }, [filteredData]);

  const { chartData, aggregation } = useMemo(() => {
    if (!filteredData.length) return { chartData: [], aggregation: 'daily' };

    let agg = 'daily';
    if (filteredData.length > 365) agg = 'monthly';
    else if (filteredData.length > 90) agg = 'weekly';

    let processed = filteredData;
    if (agg !== 'daily') {
      const groups = {};
      filteredData.forEach(d => {
        const dt = new Date(d.date + 'T00:00:00');
        let key;
        if (agg === 'weekly') {
          const start = new Date(dt);
          start.setDate(dt.getDate() - dt.getDay());
          key = start.toISOString().split('T')[0];
        } else {
          key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
        }
        if (!groups[key]) groups[key] = [];
        groups[key].push(d);
      });

      processed = Object.entries(groups)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([periodStart, items]) => {
          const avg = (field) => {
            const vals = items.filter(i => i[field] != null).map(i => i[field]);
            return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
          };
          return {
            date: periodStart,
            total_minutes_asleep: avg('total_minutes_asleep'),
            deep_minutes: avg('deep_minutes'),
            light_minutes: avg('light_minutes'),
            rem_minutes: avg('rem_minutes'),
            wake_minutes: avg('wake_minutes'),
            efficiency: avg('efficiency'),
            total_time_in_bed: avg('total_time_in_bed'),
          };
        });
    }

    const windowSize = agg === 'daily' ? 7 : agg === 'weekly' ? 4 : 3;
    const result = processed.map((d, i) => {
      const window = processed.slice(Math.max(0, i - windowSize + 1), i + 1);
      const avgVals = window.filter(w => w.total_minutes_asleep != null).map(w => w.total_minutes_asleep);
      const movingAvg = avgVals.length >= 2
        ? Math.round(avgVals.reduce((a, b) => a + b, 0) / avgVals.length)
        : null;

      return {
        date: d.date,
        deep: d.deep_minutes || 0,
        light: d.light_minutes || 0,
        rem: d.rem_minutes || 0,
        wake: d.wake_minutes || 0,
        total: d.total_minutes_asleep || 0,
        efficiency: d.efficiency,
        inBed: d.total_time_in_bed || 0,
        movingAvg,
      };
    });

    return { chartData: result, aggregation: agg };
  }, [filteredData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!allData.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-600 dark:text-gray-300">
            No sleep data available. Sync your Fitbit to see sleep data.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    if (aggregation === 'monthly') {
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    if (spanDays <= 90) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const SleepDurationTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = aggregation === 'monthly'
      ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : aggregation === 'weekly'
        ? `Week of ${new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        {aggregation !== 'daily' && (
          <p className="text-sm text-gray-400 mb-2">({aggregation} average)</p>
        )}
        <div className="space-y-1">
          <p className="text-gray-700 dark:text-gray-300">
            Total: <span className="font-semibold">{formatMinutes(d.total)}</span>
          </p>
          <p style={{ color: STAGE_COLORS.deep }}>
            Deep: <span className="font-semibold">{formatMinutes(d.deep)}</span>
          </p>
          <p style={{ color: STAGE_COLORS.light }}>
            Light: <span className="font-semibold">{formatMinutes(d.light)}</span>
          </p>
          <p style={{ color: STAGE_COLORS.rem }}>
            REM: <span className="font-semibold">{formatMinutes(d.rem)}</span>
          </p>
          <p style={{ color: STAGE_COLORS.wake }}>
            Awake: <span className="font-semibold">{formatMinutes(d.wake)}</span>
          </p>
          {d.efficiency != null && (
            <p className="text-gray-700 dark:text-gray-300 pt-1 border-t border-gray-200 dark:border-gray-600">
              Efficiency: <span className="font-semibold">{d.efficiency}%</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  const EfficiencyTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-1">
          {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
        {d.efficiency != null && (
          <p className="text-green-600">
            Efficiency: <span className="font-semibold">{d.efficiency}%</span>
          </p>
        )}
      </div>
    );
  };

  const singleDay = spanDays === 1;
  const lastNight = stats?.latest;
  const stageTotal = lastNight
    ? (lastNight.deep_minutes || 0) + (lastNight.light_minutes || 0) +
      (lastNight.rem_minutes || 0) + (lastNight.wake_minutes || 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Single-night stage breakdown — only on the 1D view. */}
      {singleDay && lastNight && stageTotal > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xl">Last Night's Sleep Stages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex rounded-lg overflow-hidden h-8 mb-4">
              {[
                { key: 'deep', val: lastNight.deep_minutes, color: STAGE_COLORS.deep },
                { key: 'light', val: lastNight.light_minutes, color: STAGE_COLORS.light },
                { key: 'rem', val: lastNight.rem_minutes, color: STAGE_COLORS.rem },
                { key: 'wake', val: lastNight.wake_minutes, color: STAGE_COLORS.wake },
              ].filter(s => s.val > 0).map(s => {
                const pct = (s.val / stageTotal) * 100;
                return (
                  <div
                    key={s.key}
                    style={{ width: `${pct}%`, backgroundColor: s.color }}
                    className="flex items-center justify-center text-sm text-white font-medium"
                    title={`${s.key}: ${formatMinutes(s.val)} (${Math.round(pct)}%)`}
                  >
                    {pct > 12 ? formatMinutes(s.val) : ''}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              {[
                { label: 'Deep', val: lastNight.deep_minutes, color: STAGE_COLORS.deep },
                { label: 'Light', val: lastNight.light_minutes, color: STAGE_COLORS.light },
                { label: 'REM', val: lastNight.rem_minutes, color: STAGE_COLORS.rem },
                { label: 'Awake', val: lastNight.wake_minutes, color: STAGE_COLORS.wake },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <div>
                    <p className="text-gray-600 dark:text-gray-300">{s.label}</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{formatMinutes(s.val)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sleep Duration Stacked Bar + Moving Avg */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle>Sleep Duration</CardTitle>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {stats?.totalNights} nights{aggregation !== 'daily' ? ` · ${aggregation} averages` : ''}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                tickFormatter={formatHoursAxis}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={48}
              />
              <Tooltip content={<SleepDurationTooltip />} />
              <Legend
                formatter={(value) => LEGEND_LABELS[value] || value}
                wrapperStyle={{ fontSize: 14 }}
              />
              <ReferenceLine
                y={480}
                stroke="#6b7280"
                strokeDasharray="3 3"
                label={{ value: '8h', position: 'insideTopLeft', fill: '#9ca3af', fontSize: 14 }}
              />
              <Bar dataKey="deep" stackId="sleep" fill={STAGE_COLORS.deep} name="deep" />
              <Bar dataKey="light" stackId="sleep" fill={STAGE_COLORS.light} name="light" />
              <Bar dataKey="rem" stackId="sleep" fill={STAGE_COLORS.rem} name="rem" radius={[2, 2, 0, 0]} />
              <Line
                dataKey="movingAvg"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                name="movingAvg"
                type="monotone"
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Sleep Efficiency */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Sleep Efficiency</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                domain={[60, 100]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={42}
              />
              <Tooltip content={<EfficiencyTooltip />} />
              <ReferenceLine
                y={85}
                stroke="#6b7280"
                strokeDasharray="3 3"
                label={{ value: '85%', position: 'insideTopLeft', fill: '#9ca3af', fontSize: 14 }}
              />
              <Line
                dataKey="efficiency"
                stroke="#22c55e"
                strokeWidth={2}
                dot={chartData.length <= 60}
                type="monotone"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default SleepChart;
